import { LitElement, html, unsafeCSS, type CSSResultGroup } from 'lit'
import { customElement, property } from 'lit/decorators.js'

// Styles:
import styles from './eco-suite-navbar-avatar.scss?inline'

/**
 * @internal
 */
@customElement('eco-suite-navbar-avatar')
export class EcoSuiteNavbarAvatar extends LitElement {
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]

  /**
   * The user's full name. The component renders up to the first two
   * whitespace-separated initials. MUST be a plain string; passing any other
   * type is unsupported.
   */
  @property()
  userName?: string

  /**
   * Indicates the avatar is rendered inside an `<eco-suite-navbar-button>`.
   * When `true`, the avatar drops its own background/text colours and inherits
   * them from the button so hover/active states flow through.
   */
  @property({ type: Boolean })
  isInButton = false

  connectedCallback() {
    super.connectedCallback()
    // The avatar is purely decorative; the accessible name is provided by the
    // surrounding label (e.g. the button's `label`). Self-enforce so consumers
    // can't accidentally surface the initials to assistive tech.
    this.setAttribute('aria-hidden', 'true')
  }

  private get initials() {
    return this.userName
      ?.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(n => n[0] ?? '')
      .join('')
  }

  render() {
    return html`
      <div class="EcoSuiteNavbarAvatar ${this.isInButton ? 'EcoSuiteNavbarAvatar--button' : ''}">
        <span>${this.initials}</span>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-avatar': EcoSuiteNavbarAvatar
  }
}
